import Link from "@/components/LocaleLink";
import {
  Fan, Thermometer, Droplet, Sparkles, PencilRuler, Wrench,
  ShieldCheck, Leaf, Users, Clock, Home, Server, ShoppingCart, Leaf as LeafIcon
} from "lucide-react";
import Header from "@/components/Header";
import { useLanguage } from "@/hooks/useLanguage";
import { usePageSEO } from "@/hooks/usePageSEO";
import heroImg from "@/assets/hero-hvac-sunset.webp";
import lgLogo from "@/assets/logo-lg.png";
import kaysunLogo from "@/assets/logo-kaysun.png";
import haierLogo from "@/assets/haier.webp";
import imgIndustrial from "@/assets/sector-warehouse.webp";
import imgRetail from "@/assets/sector-supermarket.jpg";
import imgFactory from "@/assets/sector-factory.webp";
import imgHouse from "@/assets/sector-house.webp";
import imgServer from "@/assets/sector-serverroom.webp";

const Index = () => {
  const { t, lang } = useLanguage();

  usePageSEO({
    title: t("home.metaTitle"),
    description: t("home.metaDesc"),
    path: "/",
    lang,
  });

  const services = [
    { icon: Fan, title: t("home.svc1.title"), desc: t("home.svc1.desc"), link: "/ventilacija" },
    { icon: Thermometer, title: t("home.svc2.title"), desc: t("home.svc2.desc"), link: "/apkure" },
    { icon: Droplet, title: t("home.svc3.title"), desc: t("home.svc3.desc"), link: "/konsultacijas" },
    { icon: Sparkles, title: t("home.svc4.title"), desc: t("home.svc4.desc"), link: "/kondicionieri" },
    { icon: PencilRuler, title: t("home.svc5.title"), desc: t("home.svc5.desc"), link: "/projektesana" },
    { icon: Wrench, title: t("home.svc6.title"), desc: t("home.svc6.desc"), link: "/serviss" },
  ];

  const sectors = [
    { icon: Home, title: t("home.sec1"), alt: t("home.sec1.alt"), img: imgIndustrial, link: "/industrialie-objekti" },
    { icon: ShoppingCart, title: t("home.sec2"), alt: t("home.sec2.alt"), img: imgRetail, link: "/lielveikali" },
    { icon: Droplet, title: t("home.sec3"), alt: t("home.sec3.alt"), img: imgFactory, link: "/razotnes" },
    { icon: Home, title: t("home.sec4"), alt: t("home.sec4.alt"), img: imgHouse, link: "/privatmajas" },
    { icon: Server, title: t("home.sec5"), alt: t("home.sec5.alt"), img: imgServer, link: "/servertelpas" },
  ];

  const values = [
    { icon: ShieldCheck, title: t("home.val1.title"), desc: t("home.val1.desc") },
    { icon: Leaf, title: t("home.val2.title"), desc: t("home.val2.desc") },
    { icon: Users, title: t("home.val3.title"), desc: t("home.val3.desc") },
    { icon: Clock, title: t("home.val4.title"), desc: t("home.val4.desc") },
  ];

  return (
    <div className="bg-background flex flex-col pt-20 lg:pt-[88px] xl:h-screen xl:overflow-hidden overflow-x-hidden">
      <Header />

      <main className="flex-1 flex flex-col xl:h-[calc(100vh-88px)] xl:min-h-0">
        {/* Hero */}
        <section className="min-h-[420px] xl:min-h-0 xl:h-[39%] xl:flex-none relative overflow-hidden">
          <img src={heroImg} alt="HVAC industrial systems" className="absolute inset-0 w-full h-full object-cover object-right" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-card via-card/70 via-20% to-transparent to-50%" />
          <div className="absolute inset-0 bg-gradient-to-b from-card/30 via-transparent to-card/20 lg:hidden" />

          <div className="relative grid lg:grid-cols-[minmax(0,720px)_1fr] h-full">
            <div className="flex flex-col justify-start pt-4 lg:pt-5 pl-4 lg:pl-12 pr-4 pb-4 lg:pb-2">
              <h1 className="text-3xl sm:text-4xl lg:text-[clamp(1.9rem,2.8vw,2.65rem)] font-extrabold leading-[1.05] tracking-tight text-heading lg:whitespace-nowrap">
                {t("home.hero.title1")}<br />
                {t("home.hero.title2")}
              </h1>
              <p className="text-sm lg:text-[14px] text-foreground/75 max-w-md leading-relaxed mt-2 lg:mt-3">
                {t("home.hero.desc")}
              </p>
              <div className="flex flex-wrap gap-3 mt-3 lg:mt-4">
                <Link to="/pakalpojumi" className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary text-primary-foreground text-[13px] font-bold tracking-wider hover:bg-accent transition-colors shadow-md">
                  {t("home.hero.ctaServices")} <span>›</span>
                </Link>
                <Link to="/kontakti" className="inline-flex items-center px-6 h-11 rounded-md border-2 border-primary text-primary text-[13px] font-bold tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors bg-card/70 backdrop-blur-sm">
                  {t("home.hero.ctaContact")}
                </Link>
              </div>
              {/* Mobile: stacked column, transparent, same width as buttons */}
              <div className="flex flex-col gap-2 mt-3 lg:hidden">
                <img src={haierLogo} alt="Haier HVAC systems logo" className="h-16 w-44 object-contain object-left" />
                <img src={kaysunLogo} alt="Kaysun climate technology logo" className="h-13 w-44 object-contain object-left" />
                <img src={lgLogo} alt="LG air conditioning solutions logo" className="h-11 w-44 object-contain object-left" />
              </div>
              {/* Desktop: horizontal row */}
              <div className="hidden lg:flex items-center gap-4 mt-4">
                <img src={haierLogo} alt="Haier HVAC systems logo" className="max-h-24 w-auto object-contain shrink-0" />
                <img src={kaysunLogo} alt="Kaysun climate technology logo" className="max-h-12 w-auto object-contain shrink-0" />
                <img src={lgLogo} alt="LG air conditioning solutions logo" className="max-h-12 w-auto object-contain shrink-0" />
              </div>
            </div>

            <div
              className="hidden xl:flex absolute bottom-10 right-10 w-[340px] xl:w-[370px] items-center gap-4 px-8 py-6 rounded-md shadow-2xl text-white bg-gradient-to-br from-[hsl(var(--brand-navy))] via-[hsl(var(--brand-navy))]/95 to-[hsl(220_60%_25%)]"
              style={{ clipPath: "polygon(0 0, 100% 0, 92% 100%, 0% 100%)" }}
            >
              <LeafIcon className="w-11 h-11 text-blue-200 flex-none" strokeWidth={1.25} />
              <div className="min-w-0 pr-6">
                <div className="text-[13px] font-bold tracking-widest mb-1">{t("home.card.title")}</div>
                <p className="text-[12px] text-blue-100/85 leading-snug">
                  {t("home.card.desc")}
                </p>
                <span className="block w-8 h-0.5 bg-blue-300/70 mt-2" />
              </div>
            </div>
          </div>
        </section>

        {/* Services row */}
        <section className="bg-surface-warm border-t border-border xl:h-[21%] xl:flex-none xl:min-h-0 xl:overflow-hidden">
          <div className="px-4 lg:px-8 py-6 lg:py-3 flex flex-col xl:h-full xl:min-h-0">
            <h2 className="text-center text-[12px] font-extrabold tracking-[0.2em] text-heading uppercase">
              {t("home.sectionDirections")}
              <span className="block w-10 h-0.5 bg-primary mx-auto mt-1.5" />
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 lg:gap-3 mt-3">
              {services.map((s) => (
                <Link
                  key={s.title}
                  to={s.link}
                  className="flex flex-col items-center text-center px-3 py-4 lg:py-3 rounded-lg bg-card shadow-md ring-1 ring-border/60 group transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <s.icon className="w-9 h-9 lg:w-8 lg:h-8 text-primary mb-1.5 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.25} />
                  <h3 className="text-[10px] lg:text-[11px] font-bold text-heading leading-tight uppercase tracking-wide line-clamp-2">{s.title}</h3>
                  <p className="text-[10px] lg:text-[11px] text-muted-foreground leading-snug mt-1.5 hidden sm:block line-clamp-2">{s.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sectors */}
        <section id="sektori" className="bg-secondary/40 xl:h-[33%] xl:flex-none xl:min-h-0 xl:overflow-hidden">
          <div className="px-4 lg:px-8 py-6 lg:py-3 flex flex-col xl:h-full xl:min-h-0">
            <h2 className="text-center text-[12px] font-extrabold tracking-[0.2em] text-heading uppercase">
              {t("home.sectionSectors")}
              <span className="block w-10 h-0.5 bg-primary mx-auto mt-1.5" />
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_220px] gap-3 lg:gap-4 mt-3 xl:h-full xl:min-h-0">
              {sectors.map((s) => {
                const content = (
                  <>
                    <img src={s.img} alt={s.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))]/95 via-[hsl(var(--brand-navy))]/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-2 lg:p-3 pb-4 lg:pb-6 text-white flex items-center gap-2">
                      <s.icon className="w-4 h-4 text-blue-300 flex-none" strokeWidth={1.5} />
                      <h3 className="text-[10px] lg:text-[11px] font-bold tracking-wide leading-tight text-white">{s.title}</h3>
                    </div>
                  </>
                );
                const className = `relative rounded-lg overflow-hidden group min-h-[140px] xl:min-h-0 shadow-md ring-1 ring-border/60 bg-card ${s.link ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" : "cursor-default"}`;
                return s.link ? (
                  <Link key={s.title} to={s.link} className={className}>
                    {content}
                  </Link>
                ) : (
                  <div key={s.title} className={className}>
                    {content}
                  </div>
                );
              })}
              {/* Logos share the card's remaining height and scale down with it, so
                  they stay whole at any viewport height instead of being clipped. */}
              <div className="hidden xl:flex flex-col items-center text-center gap-1.5 px-3 py-3 bg-white rounded-md shadow-lg border border-border h-full min-h-0 relative z-10 overflow-hidden">
                <p className="flex-none text-[9px] font-extrabold tracking-wider text-heading uppercase leading-tight">
                  {t("home.partners.title")}
                </p>
                <div className="flex-[1.4] min-h-0 w-full flex items-center justify-center">
                  <img src={haierLogo} alt="Haier HVAC systems logo" className="max-h-full max-w-[190px] w-auto object-contain" />
                </div>
                <div className="flex-1 min-h-0 w-full flex items-center justify-center">
                  <img src={kaysunLogo} alt="Kaysun climate technology logo" className="max-h-full max-w-[150px] w-auto object-contain" />
                </div>
                <div className="flex-1 min-h-0 w-full flex items-center justify-center">
                  <img src={lgLogo} alt="LG air conditioning solutions logo" className="max-h-full max-w-[150px] w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values footer */}
        <footer className="bg-[hsl(var(--brand-navy))] text-white xl:h-[7%] xl:flex-none xl:min-h-0">
          <div className="px-4 lg:px-8 py-6 lg:py-2 xl:h-full flex items-center">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8 items-center w-full">
              {values.map((v) => (
                <div key={v.title} className="flex items-center gap-3">
                  <v.icon className="w-8 h-8 lg:w-8 lg:h-8 text-blue-200 flex-none" strokeWidth={1.25} />
                  <div className="min-w-0">
                    <h4 className="text-[11px] font-extrabold tracking-widest text-white">{v.title}</h4>
                    <p className="text-[10px] text-blue-100/70 leading-snug mt-0.5">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Index;
